import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { isNil } from 'lodash/fp'
import { CircleDollarSignIcon, InfoIcon, PlusIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { usePromoCampaignsQuery } from '@/entities/promo-campaign'
import { VideoTutorials } from '@/entities/video-tutorial'

import DocumentTitle from '@/shared/components/DocumentTitle'
import NoData from '@/shared/components/placeholders/NoData/NoData'
import SearchField from '@/shared/components/SearchField'
import { Blanket } from '@/shared/components/ui/blanket'
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbList,
	BreadcrumbPage,
} from '@/shared/components/ui/breadcrumb'
import { Button } from '@/shared/components/ui/button'
import { Content, Header, Layout } from '@/shared/components/ui/layout'
import { Loader } from '@/shared/components/ui/loader'
import { Separator } from '@/shared/components/ui/separator'
import {
	ToggleGroup,
	ToggleGroupItem,
} from '@/shared/components/ui/toggle-group'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { VideoTag } from '@/shared/generated'
import { ampli } from '@/shared/lib/ampli'

import useStatusSelect from '../lib/useStatusSelect'
import PromoCampaignsTable from './PromoCampaignsTable'

export default function PromoCampaigns() {
	const { t } = useTranslation()

	const promoCampaigns = usePromoCampaignsQuery()
	const dataList = promoCampaigns?.data || []
	const [searchText, setSearchText] = useState('')
	useEffect(() => {
		ampli.promoOpenPromoListPage()
	}, [])
	const handleSearchChange = (searchText: string) => {
		ampli.promoSearch()
		setSearchText(searchText)
	}
	const { handleStatusSelect, getFilteredData, filteringStatus } =
		useStatusSelect()
	const filteredData = getFilteredData(dataList, searchText)

	return (
		<DocumentTitle title={t('promo_title_page')}>
			<Layout>
				<Header className='h-11 items-center border-b px-4 py-2'>
					<CircleDollarSignIcon className='size-4' />
					<Separator orientation='vertical' className='mx-2 h-5' />
					<Breadcrumb className='text-sm'>
						<BreadcrumbList>
							<BreadcrumbItem>
								<BreadcrumbPage>{t('menu.promo')}</BreadcrumbPage>
							</BreadcrumbItem>
						</BreadcrumbList>
					</Breadcrumb>
					<VideoTutorials tags={[VideoTag.Promo]}>
						<Button size='icon' className='ml-auto' variant='ghost'>
							<InfoIcon />
						</Button>
					</VideoTutorials>
				</Header>
				<Layout className='relative overflow-hidden px-4'>
					<Header className='flex items-center justify-between py-3'>
						<div className='flex items-center gap-2'>
							<SearchField
								onValueChange={handleSearchChange}
								value={searchText}
								placeholder={t('general_search')}
							/>
							<ToggleGroup
								variant='outline'
								type='single'
								value={
									filteringStatus === null
										? 'all'
										: filteringStatus === true
											? 'live'
											: 'paused'
								}
								onValueChange={(v) =>
									handleStatusSelect(v === 'all' ? null : v === 'live')
								}
							>
								<ToggleGroupItem
									className='flex-none px-4'
									value='all'
									aria-label='all'
								>
									{t('all', { defaultValue: 'All' })}
								</ToggleGroupItem>
								<ToggleGroupItem
									className='flex-none px-4'
									value='live'
									aria-label='live'
								>
									{t('app.live')}
								</ToggleGroupItem>
								<ToggleGroupItem
									className='flex-none px-4'
									value='paused'
									aria-label='paused'
								>
									{t('app.paused')}
								</ToggleGroupItem>
							</ToggleGroup>
						</div>
						<div className='ml-auto flex items-center'>
							<Tooltip>
								<TooltipTrigger asChild>
									<Button variant='primary' size='icon' asChild>
										<Link to='/p/promo/create'>
											<PlusIcon />
										</Link>
									</Button>
								</TooltipTrigger>
								<TooltipContent>{t('general_create')}</TooltipContent>
							</Tooltip>
						</div>
					</Header>
					<Content id='promo-content' className='h-full overflow-hidden pb-5'>
						<Blanket
							className='absolute flex items-center justify-center bg-white/50 backdrop-blur-lg dark:bg-black/50'
							isTinted={promoCampaigns.isLoading}
						>
							<Loader />
						</Blanket>
						<div className='border-base flex h-full flex-1 flex-col overflow-hidden rounded-lg border border-solid'>
							{filteredData?.length === 0 && promoCampaigns.isFetched ? (
								filteredData.length > 0 ||
								searchText.length > 0 ||
								!isNil(filteringStatus) ? (
									<NoData title='Looks like there are no items suitable for filters' />
								) : (
									<NoData
										title='Looks like there are no "Promo campaigns"'
										subtitle='Create new to use it for your repricing.'
									>
										<Button variant='primary' asChild>
											<Link to='/p/promo/create'>{t('general_create')}</Link>
										</Button>
									</NoData>
								)
							) : (
								<PromoCampaignsTable data={filteredData} />
							)}
						</div>
					</Content>
				</Layout>
			</Layout>
		</DocumentTitle>
	)
}
